
import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { useQuery } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { supabase } from "@/integrations/supabase/client"
import type { Database } from "@/integrations/supabase/types"

type MonthlyPrompt = Database["public"]["Tables"]["monthly_prompts"]["Row"]
type PromptType = Database["public"]["Enums"]["prompt_type"]
type SubscriptionTier = Database["public"]["Enums"]["subscription_tier"]

export default function MonthlyPromptsSection() {
  const { toast } = useToast()
  const [selectedMonth, setSelectedMonth] = useState<string>(
    (new Date().getMonth() + 1).toString()
  )
  const [selectedYear, setSelectedYear] = useState<string>(
    new Date().getFullYear().toString()
  )

  const { data: monthlyPrompts } = useQuery({
    queryKey: ["monthly_prompts", selectedMonth, selectedYear],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("monthly_prompts")
        .select("*")
        .eq("month", parseInt(selectedMonth))
        .eq("year", parseInt(selectedYear))
        .order("prompt_type")

      if (error) {
        toast({
          variant: "destructive",
          title: "Error loading monthly prompts",
          description: error.message,
        })
        return []
      }

      return data as MonthlyPrompt[]
    },
  })

  const handleSaveMonthlyPrompt = async (promptType: PromptType) => {
    const formData = new FormData(document.getElementById(`${promptType}-form`) as HTMLFormElement)
    const title = formData.get("title") as string
    const content = formData.get("content") as string
    const tier = formData.get("tier") as SubscriptionTier

    const { error } = await supabase.from("monthly_prompts").upsert({
      prompt_type: promptType,
      title,
      content,
      month: parseInt(selectedMonth),
      year: parseInt(selectedYear),
      subscription_tier: tier,
    })

    if (error) {
      toast({
        variant: "destructive",
        title: "Error saving prompt",
        description: error.message,
      })
    } else {
      toast({
        title: "Monthly prompt saved successfully",
      })
    }
  }

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ]

  const currentYear = new Date().getFullYear()
  const years = Array.from(
    { length: 3 },
    (_, i) => (currentYear + i).toString()
  )

  const tiers: SubscriptionTier[] = ["essentials", "pro", "elite"]

  return (
    <div>
      <div className="flex gap-4 mb-6">
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent>
            {months.map((month, index) => (
              <SelectItem key={index + 1} value={(index + 1).toString()}>
                {month}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {years.map((year) => (
              <SelectItem key={year} value={year}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-6">
        {["email", "text"].map((type) => (
          <Card key={type}>
            <CardHeader>
              <CardTitle>{type.charAt(0).toUpperCase() + type.slice(1)} Prompt</CardTitle>
              <CardDescription>
                Create or edit the {type} prompt for {months[parseInt(selectedMonth) - 1]} {selectedYear}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form id={`${type}-form`} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor={`${type}-title`}>Title</Label>
                  <Input
                    id={`${type}-title`}
                    name="title"
                    defaultValue={
                      monthlyPrompts?.find((p) => p.prompt_type === type)?.title
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${type}-content`}>Content</Label>
                  <Textarea
                    id={`${type}-content`}
                    name="content"
                    rows={10}
                    defaultValue={
                      monthlyPrompts?.find((p) => p.prompt_type === type)?.content
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${type}-tier`}>Subscription Tier</Label>
                  <Select
                    name="tier"
                    defaultValue={
                      monthlyPrompts?.find((p) => p.prompt_type === type)
                        ?.subscription_tier || "essentials"
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiers.map((tier) => (
                        <SelectItem key={tier} value={tier}>
                          {tier.charAt(0).toUpperCase() + tier.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  onClick={() => handleSaveMonthlyPrompt(type as PromptType)}
                  className="w-full"
                >
                  Save {type.charAt(0).toUpperCase() + type.slice(1)} Prompt
                </Button>
              </form>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
